<template>
    <div class="calendar-filters" role="region" aria-label="Event filters">
        <h2 class="calendar-filters__title">Filter</h2>
        <div class="calendar-filters__container">
            <details v-for="(filter, index) in filters" :key="index" class="calendar-filters__fieldset"
                     :open="filter.isOpen">
                <summary class="calendar-filters__summary">
                    {{ filter.title }}
                </summary>
                <fieldset class="calendar-filters__group">
                    <legend class="visually-hidden">Filter by {{ filter.title }}</legend>
                    <div class="calendar-filters__options">
                        <label v-for="facet in filter.facets" :key="facet.title" class="calendar-filters__label"
                               :class="{ 'is-disabled': facet.isDisabled }">
                            <input type="checkbox" :checked="facet.isChecked" :disabled="facet.isDisabled"
                                   @change="updateFacet(filter, facet)" class="calendar-filters__checkbox" />
                            <span>{{ facet.title }}</span>
                        </label>
                    </div>
                </fieldset>
            </details>
        </div>
        <button @click="viewResults" class="calendar-filters__view-results">
            View {{ displayCount }} results
        </button>
        <button v-if="canReset" @click="resetFilters" class="calendar-filters__reset">
            Reset Filters
        </button>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import { flatten, uniqBy, get, compact, map, debounce } from 'lodash';
import { useState } from '../services/stateful';
import Fuse from 'fuse.js';

const emit = defineEmits(['close', 'update:filteredEvents']);

const props = defineProps({
    events: {
        type: Array,
        required: true,
    },
});

const filteredEvents = useState('Calendar.FilteredEvents', []);

const FILTER_CONFIG = [
    {
        name: 'genre',
        title: 'Genre',
        field: 'event.attributes.custom.genres',
        facets: [],
        isOpen: false,
    },
    {
        name: 'timeOfDay',
        title: 'Time of Day',
        field: 'timeOfDay',
        facets: [],
        isOpen: false,
    },
    {
        name: 'venue',
        title: 'Venue',
        field: 'event.venue.title',
        facets: [],
        isOpen: false,
    },
];

const filters = ref([]);
const displayCount = ref(0);

let allPerformances = null;
let fuse = null;
let filteredPerformances = null;
let animationId = null;

const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const buildQuery = (appliedFacets) => {
    return compact(
        map(appliedFacets, (facets, filter) => {
            if (!facets.length) return null;

            return {
                '$and': facets.map(facet => ({
                    [filter]: facet,
                })),
            };
        })
    );
}

const setupFilters = (performances) => {
    return FILTER_CONFIG.map(filter => {
        let facets = performances.map(item => {
            if (filter.name === 'timeOfDay') {
                return item.timeOfDay ? [item.timeOfDay] : [];
            }

            const value = get(item, filter.field);
            return Array.isArray(value) ? value : (value ? [value] : []);
        });

        facets = flatten(facets);
        facets = uniqBy(facets, facet => facet);

        filter.facets = facets.map(facet => ({
            title: facet,
            isChecked: false,
            isDisabled: false,
        }));

        return filter;
    });
}

const canReset = computed(() => {
    return filters.value.some(filter =>
        filter.facets.some(facet => facet.isChecked)
    );
});

const processResults = () => {
    const appliedFacets = {};

    for (const filter of filters.value) {
        appliedFacets[filter.field] = [];

        for (const facet of filter.facets) {
            if (facet.isChecked) {
                appliedFacets[filter.field].push(facet.title);
            }
        }
    }

    const appliedQuery = buildQuery(appliedFacets);

    if (appliedQuery.length) {
        filteredPerformances = fuse.search({
            '$and': appliedQuery,
        }).map(result => result.item);
        canReset.value = true;
    } else {
        filteredPerformances = allPerformances;
        canReset.value = false;
    }

    const availableValues = {};
    filters.value.forEach(filter => {

        availableValues[filter.field] = new Set(
            filteredPerformances.flatMap(item => {
                const value = get(item, filter.field);

                if (Array.isArray(value)) {
                    return value;
                } else if (value !== undefined && value !== null) {
                    return [value];
                }
                return [];
            })
        );
    });

    filters.value = filters.value.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (!facet.isChecked) {
                facet.isDisabled = !availableValues[filter.field].has(facet.title);
            }

            return facet;
        });

        return filter;
    });

    animateCount(filteredPerformances ? filteredPerformances.length : props.events.length);

    filteredEvents.value = filteredPerformances;
};

const debouncedProcessResults = debounce(() => {
    processResults();
}, 150);

const animateCount = (targetCount) => {
    if (prefersReducedMotion()) {
        displayCount.value = targetCount;
        return;
    }

    if (animationId) {
        cancelAnimationFrame(animationId)
    }

    const startCount = displayCount.value;
    const difference = targetCount - startCount;
    const duration = 300; // ms
    const startTime = performance.now();

    const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        displayCount.value = Math.round(startCount + difference * progress);

        if (progress < 1) {
            animationId = requestAnimationFrame(animate);
        }
    };

    animationId = requestAnimationFrame(animate);
};

const viewResults = () => {
    emit('close');
};

const updateFacet = (filter, facet) => {
    facet.isChecked = !facet.isChecked;
    debouncedProcessResults();
};

const resetFilters = () => {
    filters.value = filters.value.map(filter => {
        filter.facets = filter.facets.map(facet => {
            facet.isChecked = false;
            return facet;
        });
        return filter;
    });

    processResults();
};

watch(() => props.events, (newEvents, oldEvents) => {
    if (newEvents.length === 0) return;
    if (newEvents === oldEvents) return;

    allPerformances = newEvents;

    fuse = new Fuse(allPerformances, {
        threshold: 0.3,
        useExtendedSearch: true,
        includeScore: false,
        shouldSort: false,
        keys: [
            'event.title',
            'event.attributes.custom.genres',
            'timeOfDay',
            'event.venue.title',
        ],
    });

    filters.value = setupFilters(allPerformances);
    displayCount.value = allPerformances.length;
    processResults();
}, { immediate: true });

</script>
